import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    Box,
    Heading,
    Input,
    InputGroup,
    InputLeftElement,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    Badge,
    VStack,
    HStack,
    Spinner,
    Center,
    useToast,
    Select,
    Card,
    CardBody,
    Flex,
    Spacer,
    Text,
    SimpleGrid,
    Stat,
    StatLabel,
    StatNumber,
    StatHelpText,
    Alert,
    AlertIcon,
    IconButton,
    Divider,
    useBreakpointValue,
} from '@chakra-ui/react';
import { SearchIcon, CheckIcon, ArrowBackIcon, CloseIcon } from '@chakra-ui/icons';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { CheckInListItem } from '../types/api';
import { useAuth } from '../contexts/AuthContext';

// ============================================
// SSE Hook for Real-Time Updates
// ============================================
const useCheckInStream = (eventId: string, enabled: boolean) => {
    const queryClient = useQueryClient();
    const toast = useToast();
    const { user, isLoading: authLoading } = useAuth();
    const [isConnected, setIsConnected] = useState(false);
    const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
    const eventSourceRef = useRef<EventSource | null>(null);

    useEffect(() => {
        // Wait for auth to load and verify user exists
        if (!enabled || !eventId || authLoading || !user) {
            console.log('SSE waiting for conditions:', { 
                enabled, 
                eventId: !!eventId, 
                authLoading, 
                user: !!user 
            });
            return;
        }

        // Get token from localStorage
        const token = localStorage.getItem('ffm_access_token');
        
        if (!token) {
            console.error('❌ No auth token found for SSE connection');
            return;
        }

        const apiUrl = process.env.REACT_APP_API_URL || '/api';
        
        console.log(`🔌 Establishing SSE connection for event ${eventId}...`);
        console.log(`   User: ${user.email}`);
        console.log(`   Token: ${token.substring(0, 20)}...`);

        // Create EventSource with token as query parameter
        // (EventSource API doesn't support custom headers)
        const eventSource = new EventSource(
            `${apiUrl}/checkin/events/${eventId}/checkin/stream?token=${token}`,
            { withCredentials: false }
        );

        eventSourceRef.current = eventSource;

        // Connection opened
        eventSource.onopen = () => {
            console.log('✅ SSE connection established');
            setIsConnected(true);
            
            toast({
                title: '🟢 Temps réel activé',
                description: 'Les mises à jour se feront automatiquement',
                status: 'success',
                duration: 2000,
                position: 'top-right',
            });
        };

        // Initial connection confirmation
        eventSource.addEventListener('connected', (event) => {
            console.log('📡 SSE connected event received:', event.data);
            try {
                const data = JSON.parse(event.data);
                console.log('   Connection data:', data);
            } catch (e) {
                console.log('   Raw connection message:', event.data);
            }
        });

        // Real-time updates
        eventSource.addEventListener('update', (event) => {
            try {
                const data = JSON.parse(event.data);
                console.log('📡 SSE update received:', {
                    type: data.type,
                    invitee_id: data.invitee_id,
                    contact_name: data.contact_name,
                    checked_in: data.checked_in,
                    stats: data.stats
                });
                
                setLastUpdate(new Date());

                // Update React Query cache for the check-in list
                queryClient.setQueryData(
                    ['checkin-list', eventId],
                    (oldData: any) => {
                        if (!oldData) {
                            console.log('   No existing cache data to update');
                            return oldData;
                        }

                        console.log('   Updating cache for invitee:', data.invitee_id);

                        // Update the specific item in the list
                        const updatedItems = oldData.items.map((item: CheckInListItem) =>
                            item.invitee_id === data.invitee_id
                                ? {
                                    ...item,
                                    checked_in: data.checked_in,
                                    checked_in_at: data.checked_in_at
                                }
                                : item
                        );

                        // Update stats if provided
                        const updatedStats = data.stats || oldData.stats;

                        console.log('   Updated stats:', updatedStats);

                        return {
                            ...oldData,
                            items: updatedItems,
                            stats: updatedStats
                        };
                    }
                );

                // Also invalidate queries to ensure consistency
                queryClient.invalidateQueries({ 
                    queryKey: ['checkin-list', eventId],
                    exact: false 
                });
                
                // Invalidate event data to update overall stats
                queryClient.invalidateQueries({ 
                    queryKey: ['event', eventId] 
                });

                // Determine toast appearance based on update type
                let emoji: string;
                let action: string;
                let status: 'success' | 'info' | 'warning';

                switch (data.type) {
                    case 'cancel':
                        emoji = '🔄';
                        action = 'Check-in annulé';
                        status = 'info';
                        break;
                    case 'qr_checkin':
                        emoji = '📱';
                        action = 'Check-in QR';
                        status = 'success';
                        break;
                    case 'checkin':
                    default:
                        emoji = '✅';
                        action = 'Check-in confirmé';
                        status = 'success';
                        break;
                }

                // Show toast notification
                toast({
                    title: `${emoji} ${data.contact_name}`,
                    description: (
                        <VStack align="start" spacing={0}>
                            <Text fontWeight="medium">{action}</Text>
                            {data.organization && (
                                <Text fontSize="sm" color="gray.600">
                                    {data.organization}
                                </Text>
                            )}
                            {data.vip_status && data.vip_status !== 'none' && (
                                <Badge colorScheme="purple" fontSize="xs" mt={1}>
                                    VIP
                                </Badge>
                            )}
                        </VStack>
                    ),
                    status: status,
                    duration: 4000,
                    position: 'top-right',
                    isClosable: true,
                });

                // Optional: Play sound notification
                try {
                    const audio = new Audio('/sounds/checkin-beep.mp3');
                    audio.volume = 0.3;
                    audio.play().catch((err) => {
                        // Silently fail if sound doesn't work (autoplay restrictions, etc.)
                        console.log('Sound notification skipped:', err.message);
                    });
                } catch (e) {
                    // Ignore sound errors completely
                }

            } catch (error) {
                console.error('❌ Error processing SSE update:', error);
                console.error('   Raw event data:', event.data);
            }
        });

        // Connection error or closed
        eventSource.onerror = (error) => {
            console.error('❌ SSE connection error:', error);
            setIsConnected(false);
            
            // Check if EventSource is still trying to reconnect
            if (eventSource.readyState === EventSource.CONNECTING) {
                console.log('🔄 SSE reconnecting...');
                toast({
                    title: '🟡 Reconnexion...',
                    description: 'Tentative de rétablissement de la connexion temps réel',
                    status: 'warning',
                    duration: 3000,
                    position: 'top-right',
                });
            } else if (eventSource.readyState === EventSource.CLOSED) {
                console.log('🔴 SSE connection closed');
                toast({
                    title: '🔴 Connexion perdue',
                    description: 'Les mises à jour temps réel sont désactivées',
                    status: 'error',
                    duration: 5000,
                    position: 'top-right',
                });
            }
        };

        // Cleanup on unmount or when dependencies change
        return () => {
            console.log('🔌 Closing SSE connection');
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
                eventSourceRef.current = null;
            }
            setIsConnected(false);
        };
    }, [eventId, enabled, authLoading, user, queryClient, toast]);

    // Provide manual reconnect function (optional)
    const reconnect = useCallback(() => {
        console.log('🔄 Manual SSE reconnect triggered');
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
            eventSourceRef.current = null;
        }
        setIsConnected(false);
        // The useEffect will recreate the connection automatically
    }, []);

    return { isConnected, lastUpdate, reconnect };
};

// ============================================
// Mobile Check-in Card Component
// ============================================
interface CheckInCardProps {
    item: CheckInListItem;
    onCheckIn: () => void;
    onCancelCheckIn: () => void;
    isCheckingIn: boolean;
    isCanceling: boolean;
}

const CheckInCard: React.FC<CheckInCardProps> = ({
    item,
    onCheckIn,
    onCancelCheckIn,
    isCheckingIn,
    isCanceling,
}) => {
    const getVIPBadgeColor = (status: string) => {
        switch (status) {
            case 'very_vip': return 'purple';
            case 'board_member': return 'red';
            case 'vip': return 'blue';
            case 'sponsor': return 'green';
            case 'media': return 'orange';
            default: return 'gray';
        }
    };

    const getVIPLabel = (status: string) => {
        switch (status) {
            case 'very_vip': return 'VIP+';
            case 'board_member': return 'Conseil';
            case 'vip': return 'VIP';
            case 'sponsor': return 'Sponsor';
            case 'media': return 'Média';
            default: return '';
        }
    };

    return (
        <Card bg={item.checked_in ? 'green.50' : 'white'}>
            <CardBody>
                <VStack align="stretch" spacing={3}>
                    {/* Header */}
                    <Flex justify="space-between" align="start">
                        <VStack align="start" spacing={1} flex="1">
                            <Text fontWeight="bold" fontSize="md">
                                {item.contact_name}
                            </Text>
                            {item.official_title && (
                                <Text fontSize="sm" color="gray.600">
                                    {item.official_title}
                                </Text>
                            )}
                            {item.contact_email && (
                                <Text fontSize="xs" color="gray.500">
                                    {item.contact_email}
                                </Text>
                            )}
                        </VStack>
                        {item.vip_status !== 'none' && (
                            <Badge colorScheme={getVIPBadgeColor(item.vip_status)}>
                                {getVIPLabel(item.vip_status)}
                            </Badge>
                        )}
                    </Flex>

                    <Divider />

                    {/* Details */}
                    <VStack align="start" spacing={2} fontSize="sm">
                        {item.organization && (
                            <HStack>
                                <Text color="gray.500" minW="80px">Organisation:</Text>
                                <Text fontWeight="medium">{item.organization}</Text>
                            </HStack>
                        )}
                        <HStack>
                            <Text color="gray.500" minW="80px">Statut:</Text>
                            {item.checked_in ? (
                                <Badge colorScheme="green" fontSize="sm">
                                    ✓ Enregistré
                                </Badge>
                            ) : (
                                <Badge colorScheme="orange" fontSize="sm">
                                    En attente
                                </Badge>
                            )}
                        </HStack>
                        {item.checked_in_at && (
                            <HStack>
                                <Text color="gray.500" minW="80px">Heure:</Text>
                                <Text fontWeight="medium">
                                    {new Date(item.checked_in_at).toLocaleTimeString('fr-FR', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}
                                </Text>
                            </HStack>
                        )}
                    </VStack>

                    <Divider />

                    {/* Action */}
                    {item.checked_in ? (
                        <Button
                            colorScheme="red"
                            variant="outline"
                            size="md"
                            leftIcon={<CloseIcon />}
                            onClick={onCancelCheckIn}
                            isLoading={isCanceling}
                            w="full"
                        >
                            Annuler le check-in
                        </Button>
                    ) : (
                        <Button
                            colorScheme="green"
                            size="lg"
                            leftIcon={<CheckIcon />}
                            onClick={onCheckIn}
                            isLoading={isCheckingIn}
                            w="full"
                        >
                            Check-in
                        </Button>
                    )}
                </VStack>
            </CardBody>
        </Card>
    );
};

// ============================================
// Main Check-In Page Component
// ============================================
const CheckInPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const { isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const toast = useToast();
    const queryClient = useQueryClient();

    // Detect screen size
    const isMobile = useBreakpointValue({ base: true, md: false });

    // Enable real-time SSE updates only after auth is ready
    const { isConnected, lastUpdate } = useCheckInStream(id!, !!id && !authLoading);
    
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const pageSize = 50;

    // Fetch event details
    const { data: event } = useQuery({
        queryKey: ['event', id],
        queryFn: () => apiService.getEvent(parseInt(id!)),
        enabled: !!id,
    });

    // Fetch check-in list
    const { data: checkInData, isLoading } = useQuery({
        queryKey: ['checkin-list', id, page, searchQuery, statusFilter],
        queryFn: () =>
            apiService.getCheckInList(parseInt(id!), {
                page,
                page_size: pageSize,
                q: searchQuery || undefined,
                status_filter: statusFilter || undefined,
            }),
        enabled: !!id,
        refetchInterval: false, // SSE handles real-time updates
    });

    // Check-in mutation
    const checkinMutation = useMutation({
        mutationFn: ({ eventId, inviteeId }: { eventId: number; inviteeId: number }) =>
            apiService.checkInInvitee(eventId, inviteeId),
        onSuccess: (data) => {
            // SSE will handle the UI update, but we still show local feedback
            if (!data.already_checked_in) {
                queryClient.invalidateQueries({ queryKey: ['checkin-list', id] });
                queryClient.invalidateQueries({ queryKey: ['event', id] });
            }

            // Don't show toast here if SSE is connected (it will show its own)
            if (!isConnected) {
                toast({
                    title: data.already_checked_in ? '⚠️ Déjà enregistré' : '✅ Check-in réussi',
                    description: data.message,
                    status: data.already_checked_in ? 'warning' : 'success',
                    duration: 3000,
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: 'Erreur',
                description: error.message || 'Impossible d\'enregistrer l\'invité',
                status: 'error',
                duration: 5000,
            });
        },
    });

    // Cancel check-in mutation
    const cancelCheckinMutation = useMutation({
        mutationFn: ({ eventId, inviteeId }: { eventId: number; inviteeId: number }) =>
            apiService.cancelCheckIn(eventId, inviteeId),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['checkin-list', id] });
            queryClient.invalidateQueries({ queryKey: ['event', id] });

            // Don't show toast if SSE is connected
            if (!isConnected) {
                toast({
                    title: '✅ Check-in annulé',
                    description: data.message,
                    status: 'success',
                    duration: 3000,
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: 'Erreur',
                description: error.message || 'Impossible d\'annuler le check-in',
                status: 'error',
                duration: 5000,
            });
        },
    });

    const handleCheckIn = (inviteeId: number) => {
        checkinMutation.mutate({
            eventId: parseInt(id!),
            inviteeId,
        });
    };

    const handleCancelCheckIn = (inviteeId: number, contactName: string) => {
        if (window.confirm(`Annuler le check-in de ${contactName} ?`)) {
            cancelCheckinMutation.mutate({
                eventId: parseInt(id!),
                inviteeId,
            });
        }
    };

    const getVIPBadgeColor = (status: string) => {
        switch (status) {
            case 'very_vip': return 'purple';
            case 'board_member': return 'red';
            case 'vip': return 'blue';
            case 'sponsor': return 'green';
            case 'media': return 'orange';
            default: return 'gray';
        }
    };

    const getVIPLabel = (status: string) => {
        switch (status) {
            case 'very_vip': return 'VIP+';
            case 'board_member': return 'Conseil';
            case 'vip': return 'VIP';
            case 'sponsor': return 'Sponsor';
            case 'media': return 'Média';
            case 'none': return '';
            default: return status;
        }
    };

    return (
        <Box>
            {/* Header - Responsive */}
            <VStack spacing={3} mb={6} align="stretch">
                <Flex align="center" justify="space-between">
                    <VStack align="start" spacing={1} flex="1" minW={0}>
                        <HStack spacing={2} flexWrap="wrap">
                            <Heading size={{ base: 'md', md: 'lg' }}>Check-in</Heading>
                            {event && (
                                <Badge colorScheme="blue" fontSize={{ base: 'xs', md: 'sm' }}>
                                    {event.title}
                                </Badge>
                            )}
                        </HStack>
                        {event && (
                            <Text 
                                color="gray.600" 
                                fontSize={{ base: 'xs', md: 'sm' }}
                                noOfLines={2}
                            >
                                📅 {new Date(event.event_date).toLocaleDateString('fr-FR', {
                                    weekday: isMobile ? 'short' : 'long',
                                    year: 'numeric',
                                    month: isMobile ? 'short' : 'long',
                                    day: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                })}
                            </Text>
                        )}
                    </VStack>
                    <IconButton
                        icon={<ArrowBackIcon />}
                        aria-label="Retour"
                        variant="ghost"
                        size={{ base: 'md', md: 'md' }}
                        onClick={() => navigate(`/events/${id}`)}
                    />
                </Flex>
            </VStack>

            {/* Connection Status Alert */}
            <Alert 
                status={isConnected ? "success" : "warning"} 
                mb={4}
                size={{ base: 'sm', md: 'md' }}
            >
                <AlertIcon />
                <VStack align="start" spacing={0} flex="1">
                    <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="medium">
                        {isConnected ? (
                            <>🟢 Mises à jour en temps réel actives</>
                        ) : (
                            <>🟡 Connexion temps réel en cours...</>
                        )}
                    </Text>
                    {lastUpdate && (
                        <Text fontSize="xs" color="gray.600">
                            Dernière MAJ: {lastUpdate.toLocaleTimeString('fr-FR', {
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                            })}
                        </Text>
                    )}
                </VStack>
            </Alert>

            {/* Stats Cards - Responsive Grid */}
            {checkInData && (
                <SimpleGrid 
                    columns={{ base: 1, sm: 3 }} 
                    spacing={{ base: 3, md: 6 }} 
                    mb={6}
                >
                    <Card>
                        <CardBody py={{ base: 3, md: 4 }}>
                            <Stat>
                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>
                                    Total attendus
                                </StatLabel>
                                <StatNumber fontSize={{ base: 'xl', md: '2xl' }}>
                                    {checkInData.stats.total_accepted}
                                </StatNumber>
                                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                                    Invités présents
                                </StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody py={{ base: 3, md: 4 }}>
                            <Stat>
                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>
                                    Enregistrés
                                </StatLabel>
                                <StatNumber 
                                    color="green.500" 
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                >
                                    {checkInData.stats.checked_in}
                                </StatNumber>
                                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                                    {checkInData.stats.total_accepted > 0
                                        ? Math.round(
                                            (checkInData.stats.checked_in / checkInData.stats.total_accepted) * 100
                                        )
                                        : 0}
                                    % du total
                                </StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>

                    <Card>
                        <CardBody py={{ base: 3, md: 4 }}>
                            <Stat>
                                <StatLabel fontSize={{ base: 'xs', md: 'sm' }}>
                                    En attente
                                </StatLabel>
                                <StatNumber 
                                    color="orange.500" 
                                    fontSize={{ base: 'xl', md: '2xl' }}
                                >
                                    {checkInData.stats.pending}
                                </StatNumber>
                                <StatHelpText fontSize={{ base: 'xs', md: 'sm' }}>
                                    Pas encore arrivés
                                </StatHelpText>
                            </Stat>
                        </CardBody>
                    </Card>
                </SimpleGrid>
            )}

            {/* Filters - Responsive */}
            <Card mb={6}>
                <CardBody>
                    {/* Mobile: Vertical Stack */}
                    <VStack 
                        spacing={3} 
                        align="stretch"
                        display={{ base: 'flex', md: 'none' }}
                    >
                        <InputGroup>
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Rechercher..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                autoComplete="off"
                                size="md"
                            />
                        </InputGroup>

                        <Select
                            placeholder="Tous les statuts"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            size="md"
                        >
                            <option value="not_checked_in">Non enregistrés</option>
                            <option value="checked_in">Enregistrés</option>
                        </Select>

                        {(searchQuery || statusFilter) && (
                            <Button
                                size="sm"
                                onClick={() => {
                                    setSearchQuery('');
                                    setStatusFilter('');
                                    setPage(1);
                                }}
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </VStack>

                    {/* Desktop: Horizontal Stack */}
                    <HStack 
                        spacing={4}
                        display={{ base: 'none', md: 'flex' }}
                    >
                        <InputGroup maxW="400px">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input
                                placeholder="Rechercher par nom, email, organisation..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                autoComplete="off"
                            />
                        </InputGroup>

                        <Select
                            placeholder="Tous les statuts"
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(1);
                            }}
                            maxW="200px"
                        >
                            <option value="not_checked_in">Non enregistrés</option>
                            <option value="checked_in">Enregistrés</option>
                        </Select>

                        {(searchQuery || statusFilter) && (
                            <Button
                                size="sm"
                                onClick={() => {
                                    setSearchQuery('');
                                    setStatusFilter('');
                                    setPage(1);
                                }}
                            >
                                Réinitialiser
                            </Button>
                        )}
                    </HStack>
                </CardBody>
            </Card>

            {/* Check-in List - Mobile Cards / Desktop Table */}
            {isLoading ? (
                <Center py={10}>
                    <Spinner size="xl" />
                </Center>
            ) : !checkInData?.items || checkInData.items.length === 0 ? (
                <Card>
                    <CardBody>
                        <Box p={8} textAlign="center">
                            <Text color="gray.500">Aucun invité trouvé</Text>
                        </Box>
                    </CardBody>
                </Card>
            ) : isMobile ? (
                // Mobile: Card View
                <>
                    <VStack spacing={3} align="stretch">
                        {checkInData.items.map((item: CheckInListItem) => (
                            <CheckInCard
                                key={item.invitee_id}
                                item={item}
                                onCheckIn={() => handleCheckIn(item.invitee_id)}
                                onCancelCheckIn={() => handleCancelCheckIn(item.invitee_id, item.contact_name)}
                                isCheckingIn={checkinMutation.isPending}
                                isCanceling={cancelCheckinMutation.isPending}
                            />
                        ))}
                    </VStack>

                    {/* Mobile Pagination */}
                    {checkInData.total_pages > 1 && (
                        <Flex justify="center" mt={4}>
                            <HStack spacing={2}>
                                <Button
                                    size="sm"
                                    isDisabled={page === 1}
                                    onClick={() => setPage(page - 1)}
                                >
                                    ←
                                </Button>
                                <Text fontSize="sm" px={2}>
                                    {page}/{checkInData.total_pages}
                                </Text>
                                <Button
                                    size="sm"
                                    isDisabled={page === checkInData.total_pages}
                                    onClick={() => setPage(page + 1)}
                                >
                                    →
                                </Button>
                            </HStack>
                        </Flex>
                    )}
                </>
            ) : (
                // Desktop: Table View
                <Card>
                    <CardBody p={0}>
                        <Table variant="simple">
                            <Thead>
                                <Tr>
                                    <Th>Invité</Th>
                                    <Th>Organisation</Th>
                                    <Th>VIP</Th>
                                    <Th>Statut</Th>
                                    <Th>Heure</Th>
                                    <Th>Action</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {checkInData.items.map((item: CheckInListItem) => (
                                    <Tr
                                        key={item.invitee_id}
                                        bg={item.checked_in ? 'green.50' : undefined}
                                    >
                                        <Td>
                                            <VStack align="start" spacing={0}>
                                                <Text fontWeight="medium">{item.contact_name}</Text>
                                                {item.official_title && (
                                                    <Text fontSize="sm" color="gray.600">
                                                        {item.official_title}
                                                    </Text>
                                                )}
                                                {item.contact_email && (
                                                    <Text fontSize="xs" color="gray.500">
                                                        {item.contact_email}
                                                    </Text>
                                                )}
                                            </VStack>
                                        </Td>
                                        <Td>{item.organization || '-'}</Td>
                                        <Td>
                                            {item.vip_status !== 'none' && (
                                                <Badge colorScheme={getVIPBadgeColor(item.vip_status)}>
                                                    {getVIPLabel(item.vip_status)}
                                                </Badge>
                                            )}
                                        </Td>
                                        <Td>
                                            {item.checked_in ? (
                                                <Badge colorScheme="green" fontSize="md">
                                                    ✓ Enregistré
                                                </Badge>
                                            ) : (
                                                <Badge colorScheme="orange">En attente</Badge>
                                            )}
                                        </Td>
                                        <Td>
                                            {item.checked_in_at
                                                ? new Date(item.checked_in_at).toLocaleTimeString('fr-FR', {
                                                    hour: '2-digit',
                                                    minute: '2-digit',
                                                })
                                                : '-'}
                                        </Td>
                                        <Td>
                                            {item.checked_in ? (
                                                <HStack spacing={2}>
                                                    <Badge colorScheme="green" px={3} py={1}>
                                                        ✓ Enregistré
                                                    </Badge>
                                                    <Button
                                                        size="sm"
                                                        colorScheme="red"
                                                        variant="outline"
                                                        isLoading={cancelCheckinMutation.isPending}
                                                        onClick={() => handleCancelCheckIn(item.invitee_id, item.contact_name)}
                                                    >
                                                        Annuler
                                                    </Button>
                                                </HStack>
                                            ) : (
                                                <Button
                                                    size="sm"
                                                    colorScheme="green"
                                                    leftIcon={<CheckIcon />}
                                                    isLoading={checkinMutation.isPending}
                                                    onClick={() => handleCheckIn(item.invitee_id)}
                                                >
                                                    Check-in
                                                </Button>
                                            )}
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>

                        {/* Desktop Pagination */}
                        {checkInData.total_pages > 1 && (
                            <Flex justify="center" p={4}>
                                <HStack>
                                    <Button
                                        size="sm"
                                        isDisabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    >
                                        Précédent
                                    </Button>
                                    <Text px={4} fontSize="sm">
                                        Page {page} sur {checkInData.total_pages} ({checkInData.total} invités)
                                    </Text>
                                    <Button
                                        size="sm"
                                        isDisabled={page === checkInData.total_pages}
                                        onClick={() => setPage(page + 1)}
                                    >
                                        Suivant
                                    </Button>
                                </HStack>
                            </Flex>
                        )}
                    </CardBody>
                </Card>
            )}
        </Box>
    );
};

export default CheckInPage;